import { useCallback, useEffect, useRef } from "react";
import { ContentEditableEvent } from "react-contenteditable";
import { brToParagraph, paragraphToBr } from "../../utils/editableContentTransforms";
import { AuthoryColorSchemes } from "../ProfileHero/V3ProfileHero.types";
import { GenericContentEditableWrapper } from "./GenericContentEditable.styles";
import type sanitizeHtml from "sanitize-html";
import { throttle } from "lodash";
import { decodeEntities } from "../../utils/decodeHTMLEntities";
import { useForceUpdate } from "../../hooks/useForceUpdate";
import { loadSanitizeHtml } from "../../utils/loadSanitizeHtml";

interface GenericContentEditableProps {
    colorVariant: AuthoryColorSchemes;
    text: string;
    editMode: boolean;
    placeholder?: string;
    onEditCallback: (value: string) => void;
    customBackgroundColor?: string;
}

const sanitizeConf: sanitizeHtml.IOptions = {
    allowedTags: ["br"],
    allowedAttributes: {}
};

export const GenericContentEditable = ({
    colorVariant,
    text,
    editMode,
    onEditCallback,
    placeholder = "Add a heading",
    customBackgroundColor
}: GenericContentEditableProps) => {
    const htmlRef = useRef(paragraphToBr(text || ""));

    const throttledHTMLEditEnd = throttle(onEditCallback, 1000, { leading: false });
    const callbackThrottledHTMLEditEnd = useCallback(throttledHTMLEditEnd, []);

    const forceUpdate = useForceUpdate();

    const onHTMLUpdate = (value: string) => {
        callbackThrottledHTMLEditEnd.cancel();
        callbackThrottledHTMLEditEnd(decodeEntities(brToParagraph(value)).trim());
    };

    useEffect(() => {
        forceUpdate();
    }, [text]);

    useEffect(() => {
        if (editMode) loadSanitizeHtml();
    }, [editMode]);

    return (
        <GenericContentEditableWrapper
            suppressHydrationWarning
            $colorVariant={colorVariant}
            html={htmlRef.current || ""}
            onChange={async (evt: ContentEditableEvent) => {
                const value = evt.target.value;
                const sanitizeHtml = await loadSanitizeHtml();
                const sanitizedVal = sanitizeHtml(value, sanitizeConf);
                if (sanitizedVal === htmlRef.current && value === htmlRef.current) return;
                htmlRef.current = sanitizedVal === "<br />" ? "" : sanitizedVal;
                onHTMLUpdate(sanitizedVal);
            }}
            disabled={!editMode}
            placeholder={placeholder}
            $dashOnHover={editMode}
            $editMode={editMode}
            $customBackgroundColor={customBackgroundColor}
        />
    );
};
